import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslate, useCustom, useCustomMutation } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { getCountryOptions } from "../../utils/countries";
import {
  Alert,
  Form,
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  Divider,
  Button,
  Card,
  Spin,
  message,
  Typography,
  Upload,
  Image,
  ColorPicker,
  Segmented,
  Tag,
  Space,
  Tooltip,
  theme,
  Flex,
} from "antd";
import type { UploadProps } from "antd";
import {
  HomeOutlined,
  StarOutlined,
  SettingOutlined,
  CreditCardOutlined,
  BgColorsOutlined,
  EditOutlined,
  PictureOutlined,
  FileTextOutlined,
  UploadOutlined,
  DeleteOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { usePageStyles } from "../../shared/styles";
import { GlassCard } from "../../shared/components";
import { QontoIntegrationCard } from "./components/QontoIntegrationCard";
import { StripeIntegrationCard } from "./components/StripeIntegrationCard";

const { Title, Text } = Typography;


interface IInvoiceSettings {
  id?: number;
  tenantId?: number;
  companyName?: string;
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  siren?: string;
  nafCode?: string;
  vatNumber?: string;
  iban?: string;
  bic?: string;
  defaultVatRate?: number;
  logoUrl?: string;
  logoBase64?: string;
  brandColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  headerConfig?: {
    fr?: string;
    en?: string;
  };
  footerConfig?: {
    fr?: string;
    en?: string;
  };
  vatExemptionNotice?: {
    fr?: string;
    en?: string;
  };
  paymentTermDays?: number;
  defaultLanguage?: string;
  paymentEngine?: 'stripe' | 'qonto';
  invoicingEngine?: 'internal' | 'qonto';
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string" && value.trim() === "") {
    return true;
  }
  return false;
}

function omitEmptyValues<T>(value: T): T | undefined {
  if (isEmptyValue(value)) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const filtered = value
      .map((item) => omitEmptyValues(item))
      .filter((item): item is NonNullable<typeof item> => item !== undefined);
    return filtered.length > 0 ? (filtered as T) : undefined;
  }
  if (typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      const cleaned = omitEmptyValues(nested);
      if (cleaned !== undefined) {
        result[key] = cleaned;
      }
    }
    return Object.keys(result).length > 0 ? (result as T) : undefined;
  }
  return value;
}

interface InvoiceSettingsProps {
  /**
   * Which half of the page to render.
   *  - "invoicing"    — company info, personalization, defaults (the Segmented lives here)
   *  - "integrations" — payment engine + Qonto + Stripe
   *  - undefined      — everything, the original standalone page
   *
   * Splitting these apart is what unblocks the Qonto coupling: the integrations
   * form must stay editable while Qonto is driving the invoicing fields.
   */
  section?: "invoicing" | "integrations";
  /** Hub pages render their own title. */
  hideHeader?: boolean;
}

export const InvoiceSettings: React.FC<InvoiceSettingsProps> = ({
  section,
  hideHeader,
}) => {
  const translate = useTranslate();
  const { i18n } = useTranslation();
  const countryOptions = useMemo(
    () => getCountryOptions(i18n.language),
    [i18n.language],
  );
  const [form] = Form.useForm();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [activeSegment, setActiveSegment] = useState<string>(
    section === "integrations" ? "integrations" : "company",
  );
  const [searchParams] = useSearchParams();
  const { token } = theme.useToken();
  const pageStyles = usePageStyles();

  // Fetch invoice settings
  const {
    data: invoiceSettingsData,
    isLoading,
    refetch,
  } = useCustom<IInvoiceSettings>({
    url: "invoice-settings",
    method: "get",
    queryOptions: {
      retry: false,
    },
  });

  // Mutation for saving invoice settings
  const { mutate: saveInvoiceSettings, isLoading: saving } =
    useCustomMutation();

  const { data: qontoStatusData } = useCustom<{ connected: boolean }>({
    url: 'integrations/qonto',
    method: 'get',
    queryOptions: { retry: false, staleTime: 30_000 },
  });
  const qontoConnected = qontoStatusData?.data?.connected === true;

  // Form.useWatch keeps this reactive: getFieldValue read during render is not.
  const selectedPaymentEngine = Form.useWatch('paymentEngine', form);
  const selectedInvoicingEngine = Form.useWatch('invoicingEngine', form);

  useEffect(() => {
    if (searchParams.get("qonto")) {
      setActiveSegment("integrations");
    }
  }, [searchParams]);

  useEffect(() => {
    if (
      selectedInvoicingEngine !== "internal" &&
      activeSegment === "personalization"
    ) {
      setActiveSegment("company");
    }
  }, [selectedInvoicingEngine, activeSegment]);

  // Populate form when data is loaded
  useEffect(() => {
    if (invoiceSettingsData?.data) {
      const data = invoiceSettingsData.data;
      form.setFieldsValue({
        companyName: data.companyName,
        "address.street": data.address?.street,
        "address.city": data.address?.city,
        "address.postalCode": data.address?.postalCode,
        "address.country": data.address?.country,
        siren: data.siren,
        nafCode: data.nafCode,
        vatNumber: data.vatNumber,
        iban: data.iban,
        bic: data.bic,
        defaultVatRate: data.defaultVatRate ?? 20,
        logoUrl: data.logoUrl,
        "brandColors.primary": data.brandColors?.primary,
        "brandColors.secondary": data.brandColors?.secondary,
        "brandColors.accent": data.brandColors?.accent,
        "headerConfig.fr": data.headerConfig?.fr,
        "headerConfig.en": data.headerConfig?.en,
        "footerConfig.fr": data.footerConfig?.fr,
        "footerConfig.en": data.footerConfig?.en,
        "vatExemptionNotice.fr": data.vatExemptionNotice?.fr,
        "vatExemptionNotice.en": data.vatExemptionNotice?.en,
        paymentTermDays: data.paymentTermDays ?? 0,
        defaultLanguage: data.defaultLanguage ?? "fr",
        paymentEngine: data.paymentEngine ?? 'stripe',
        invoicingEngine: data.invoicingEngine ?? 'internal',
      });
      // Set logo preview from existing data
      if (data.logoBase64) {
        setLogoPreview(data.logoBase64);
        setLogoBase64(data.logoBase64);
      } else if (data.logoUrl) {
        setLogoPreview(data.logoUrl);
      }
    }
  }, [invoiceSettingsData, form]);

  // Convert file to base64
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle logo file upload
  const handleLogoUpload: UploadProps["beforeUpload"] = async (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error(
        translate(
          "tenants.invoiceSettings.errors.invalidImageType",
          "Please upload an image file",
        ),
      );
      return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(
        translate(
          "tenants.invoiceSettings.errors.imageTooLarge",
          "Image must be smaller than 2MB",
        ),
      );
      return Upload.LIST_IGNORE;
    }

    try {
      const base64 = await convertToBase64(file);
      setLogoBase64(base64);
      setLogoPreview(base64);
    } catch (error) {
      message.error(
        translate(
          "tenants.invoiceSettings.errors.uploadFailed",
          "Failed to process image",
        ),
      );
    }

    return false; // Prevent default upload behavior
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoBase64(null);
    setLogoPreview(null);
    form.setFieldValue("logoUrl", undefined);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Transform flat form values to nested structure
      const payload = {
        companyName: values.companyName,
        address: {
          street: values["address.street"],
          city: values["address.city"],
          postalCode: values["address.postalCode"],
          country: values["address.country"],
        },
        siren: values.siren,
        nafCode: values.nafCode,
        vatNumber: values.vatNumber,
        iban: values.iban,
        bic: values.bic,
        defaultVatRate: values.defaultVatRate,
        logoUrl: values.logoUrl || undefined,
        logoBase64: logoBase64 || undefined,
        brandColors: values["brandColors.primary"]
          ? {
              primary: values["brandColors.primary"],
              secondary: values["brandColors.secondary"],
              accent: values["brandColors.accent"],
            }
          : undefined,
        headerConfig:
          values["headerConfig.fr"] || values["headerConfig.en"]
            ? {
                fr: values["headerConfig.fr"] || "",
                en: values["headerConfig.en"] || "",
              }
            : undefined,
        footerConfig:
          values["footerConfig.fr"] || values["footerConfig.en"]
            ? {
                fr: values["footerConfig.fr"] || "",
                en: values["footerConfig.en"] || "",
              }
            : undefined,
        vatExemptionNotice:
          values["vatExemptionNotice.fr"] || values["vatExemptionNotice.en"]
            ? {
                fr: values["vatExemptionNotice.fr"] || "",
                en: values["vatExemptionNotice.en"] || "",
              }
            : undefined,
        paymentTermDays: values.paymentTermDays,
        defaultLanguage: values.defaultLanguage,
        paymentEngine: values.paymentEngine,
        invoicingEngine: values.invoicingEngine,
      };

      const cleanedPayload = omitEmptyValues(payload) ?? {};

      saveInvoiceSettings(
        {
          url: "invoice-settings",
          method: "patch",
          values: cleanedPayload,
        },
        {
          onSuccess: () => {
            message.success(
              translate(
                "tenants.invoiceSettings.messages.saveSuccess",
                "Invoice settings saved successfully",
              ),
            );
            refetch();
          },
          onError: () => {
            message.error(
              translate(
                "tenants.invoiceSettings.messages.saveError",
                "Failed to save invoice settings",
              ),
            );
          },
        },
      );
    } catch (error) {
      // Form validation error
      console.error("Validation failed:", error);
    }
  };

  const resetToSaved = () => {
    const data = invoiceSettingsData?.data;
    if (data) {
      form.setFieldsValue({
        companyName: data.companyName,
        "address.street": data.address?.street,
        "address.city": data.address?.city,
        "address.postalCode": data.address?.postalCode,
        "address.country": data.address?.country,
        siren: data.siren,
        nafCode: data.nafCode,
        vatNumber: data.vatNumber,
        iban: data.iban,
        bic: data.bic,
        defaultVatRate: data.defaultVatRate ?? 20,
        logoUrl: data.logoUrl,
        "brandColors.primary": data.brandColors?.primary,
        "brandColors.secondary": data.brandColors?.secondary,
        "brandColors.accent": data.brandColors?.accent,
        "headerConfig.fr": data.headerConfig?.fr,
        "headerConfig.en": data.headerConfig?.en,
        "footerConfig.fr": data.footerConfig?.fr,
        "footerConfig.en": data.footerConfig?.en,
        "vatExemptionNotice.fr": data.vatExemptionNotice?.fr,
        "vatExemptionNotice.en": data.vatExemptionNotice?.en,
        paymentTermDays: data.paymentTermDays ?? 0,
        defaultLanguage: data.defaultLanguage ?? "fr",
      });
      if (data.logoBase64) {
        setLogoPreview(data.logoBase64);
        setLogoBase64(data.logoBase64);
      } else if (data.logoUrl) {
        setLogoPreview(data.logoUrl);
      } else {
        setLogoPreview(null);
        setLogoBase64(null);
      }
    } else {
      form.resetFields();
      setLogoPreview(null);
      setLogoBase64(null);
    }
  };

  const optionalLabel = (label: string) => (
    <span className="flex items-center gap-1">
      {label}
      <Tag color="default" className="text-xs font-normal">
        {translate("invoice-settings.optional", "Optionnel")}
      </Tag>
    </span>
  );

  const labelWithTooltip = (label: string, tooltipKey: string) => (
    <span className="flex items-center gap-1">
      {label}
      <Tooltip title={translate(tooltipKey)}>
        <InfoCircleOutlined className="text-neutral-400" />
      </Tooltip>
    </span>
  );

  const pageHeader = (
    <header className="mb-6">
      <Title
        level={2}
        className="!mb-1 !font-bold !text-2xl md:!text-3xl"
        style={{ color: token.colorText }}
      >
        {translate("invoice-settings.title", "Facturation")}
      </Title>
      <Text className="text-base" style={{ color: token.colorTextSecondary }}>
        {translate(
          "invoice-settings.description",
          "Configurez vos informations de facturation et personnalisez vos documents",
        )}
      </Text>
    </header>
  );

  const qontoActive = qontoConnected && selectedInvoicingEngine === 'qonto';
  // Qonto owns the invoicing fields when active, but never the integrations form —
  // otherwise the user could not disconnect it.
  const invoicingLocked = qontoActive && activeSegment !== 'integrations';
  const showIntegrations = section !== "invoicing";
  const showInvoicing = section !== "integrations";
  const personalizationEnabled = selectedInvoicingEngine === 'internal';
  const qontoEngineSelected =
    selectedPaymentEngine === 'qonto' || selectedInvoicingEngine === 'qonto';

  const handleInvoicingEngineChange = (value: string) => {
    if (value === 'internal') {
      form.setFieldsValue({
        invoicingEngine: 'internal',
        paymentEngine: 'stripe',
      });
      return;
    }
    form.setFieldsValue({ invoicingEngine: value });
  };

  const handlePaymentEngineChange = (value: string) => {
    if (value === 'qonto') {
      form.setFieldsValue({
        paymentEngine: 'qonto',
        invoicingEngine: 'qonto',
      });
      return;
    }
    form.setFieldsValue({ paymentEngine: value });
  };

  return (
    <div className={hideHeader ? undefined : "max-w-7xl mx-auto"}>
      <Form form={form} layout="vertical" disabled={invoicingLocked}>
        <Spin spinning={isLoading || saving}>
          {!hideHeader && pageHeader}

          {qontoActive && showInvoicing && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={translate('invoice-settings.qontoActive.title')}
              description={
                <>
                  {translate('invoice-settings.qontoActive.description')}{' '}
                  <a href="https://app.qonto.com" target="_blank" rel="noopener noreferrer">
                    {translate('invoice-settings.qontoActive.link')}
                  </a>
                  {section === "invoicing" && (
                    <>
                      {" — "}
                      <Link to="/configuration/organization?tab=integrations">
                        {translate(
                          "invoice-settings.qontoActive.manageLink",
                          "Gérer l'intégration",
                        )}
                      </Link>
                    </>
                  )}
                </>
              }
            />
          )}

          {showInvoicing && (
          <div
            className="mb-4 [&_.ant-segmented-thumb]:!bg-[var(--segmented-primary)] [&_.ant-segmented-item-selected]:!bg-[var(--segmented-primary)] [&_.ant-segmented-item-selected]:!text-white [&_.ant-segmented-item]:py-2.5 [&_.ant-segmented-item]:px-4"
            style={{ ["--segmented-primary" as string]: token.colorPrimary }}
          >
            <Segmented
              value={activeSegment}
              onChange={(v) => setActiveSegment(v as string)}
              options={[
                {
                  label: (
                    <span className="flex items-center gap-2">
                      <HomeOutlined />
                      {translate(
                        "invoice-settings.tabs.companyInfo",
                        "Informations société",
                      )}
                    </span>
                  ),
                  value: "company",
                },
                {
                  label: (
                    <Tooltip
                      title={
                        !personalizationEnabled
                          ? translate(
                              "invoice-settings.personalizationDisabledTooltip",
                              "La personnalisation n'est disponible qu'avec le moteur de facturation interne",
                            )
                          : undefined
                      }
                    >
                      <span className="flex items-center gap-2">
                        <StarOutlined />
                        {translate(
                          "invoice-settings.tabs.personalization",
                          "Personnalisation",
                        )}
                      </span>
                    </Tooltip>
                  ),
                  value: "personalization",
                  disabled: !personalizationEnabled,
                },
                {
                  label: (
                    <span className="flex items-center gap-2">
                      <SettingOutlined />
                      {translate(
                        "invoice-settings.tabs.defaultParams",
                        "Paramètres par défaut",
                      )}
                    </span>
                  ),
                  value: "defaults",
                },
                // The integrations segment only exists on the standalone page;
                // inside the Organisation hub it is a sibling tab instead.
                ...(section === undefined
                  ? [
                      {
                        label: (
                          <span className="flex items-center gap-2">
                            <ApiOutlined />
                            {translate(
                              "invoice-settings.tabs.integrations",
                              "Intégrations",
                            )}
                          </span>
                        ),
                        value: "integrations",
                      },
                    ]
                  : []),
              ]}
              className="w-full"
            />
          </div>
          )}

          <GlassCard style={pageStyles.stepperCard}>
            {showInvoicing && activeSegment === "company" && (
              <>
                <Title level={5} className="mt-2 !mb-6">
                  <HomeOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.companyInfo",
                    "Informations de l'entreprise",
                  )}
                </Title>

                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.companyName",
                        "Raison sociale",
                      )}
                      name="companyName"
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "tenants.invoiceSettings.errors.companyNameRequired",
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.companyName",
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={optionalLabel(
                        translate(
                          "tenants.invoiceSettings.fields.nafCode",
                          "Code NAF",
                        ),
                      )}
                      name="nafCode"
                      extra={translate(
                        "invoice-settings.nafCodeHelper",
                        "Code d'activité principale",
                      )}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.nafCode",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.siren",
                        "SIREN/N° d'immatriculation",
                      )}
                      name="siren"
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "tenants.invoiceSettings.errors.sirenRequired",
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.siren",
                        )}
                        maxLength={9}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.vatNumber",
                        "N° TVA intracommunautaire",
                      )}
                      name="vatNumber"
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "tenants.invoiceSettings.errors.vatNumberRequired",
                            "Le numéro de TVA est requis",
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.vatNumber",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider></Divider>

                <Title level={5} className="!my-6">
                  <EnvironmentOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.address",
                    "Adresse de facturation",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.street",
                        "Rue",
                      )}
                      name="address.street"
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "tenants.invoiceSettings.errors.streetRequired",
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.street",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.postalCode",
                        "Code postal",
                      )}
                      name="address.postalCode"
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "tenants.invoiceSettings.errors.postalCodeRequired",
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.postalCode",
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.city",
                        "Ville",
                      )}
                      name="address.city"
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "tenants.invoiceSettings.errors.cityRequired",
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.city",
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.country",
                        "Pays",
                      )}
                      name="address.country"
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "tenants.invoiceSettings.errors.countryRequired",
                          ),
                        },
                      ]}
                    >
                      <Select
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.country",
                        )}
                        options={countryOptions}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider></Divider>

                <Title level={5} className="!my-6">
                  <CreditCardOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.bankingInfo",
                    "Informations bancaires",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={optionalLabel(
                        translate(
                          "tenants.invoiceSettings.fields.iban",
                          "IBAN",
                        ),
                      )}
                      name="iban"
                      extra={translate(
                        "invoice-settings.ibanHelper",
                        "Utilisé pour les paiements par virement",
                      )}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.iban",
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={optionalLabel(
                        translate(
                          "tenants.invoiceSettings.fields.bic",
                          "BIC/SWIFT",
                        ),
                      )}
                      name="bic"
                      extra={translate(
                        "invoice-settings.bicHelper",
                        "Code d'identification bancaire",
                      )}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.bic",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {showInvoicing && activeSegment === "personalization" && (
              <>
                <Title level={5} className="!my-6">
                  <PictureOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.companyLogo",
                    "Logo de l'entreprise",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.logo",
                        "Logo",
                      )}
                    >
                      <div className="flex flex-col gap-3">
                        {logoPreview ? (
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-40 h-40 rounded border border-neutral-600 flex items-center justify-center bg-neutral-800 overflow-hidden">
                              <Image
                                src={logoPreview}
                                alt="Logo"
                                style={{
                                  maxHeight: 160,
                                  maxWidth: 160,
                                  objectFit: "contain",
                                }}
                                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgesAdsqrnwCCqjkIAAAAlmVYSWZNTQAqAAAACAAFARIAAwAAAAEAAQAAARoABQAAAAEAAABKARsABQAAAAEAAABSASgAAwAAAAEAAgAAh2kABAAAAAEAAABaAAAAAAAAAEgAAAABAAAASAAAAAEAA5KGAAcAAAASAAAAhKACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAABBU0NJSQAAAFNjcmVlbnNob3T/2wBDABALDA4MChAODQ4SERATGCgaGBYWGDEjJR0oOjM9PDkzODdASFxOQERXRTc4UG1RV19iZ2hnPk1xeXBkeFxlZ2P/wgALCADDAMIBAREA/8QAGQABAAMBAQAAAAAAAAAAAAAAAAECAwQF/9oACAEBAAAAAOgAAAAAAAAAAAAAAAAAAABIAAAAAAAAAAT5+zQAAAAABHn9wAAAAACPN6gAAAAAAjz+sAAAAAAI4uwAAAAABHB2AAAAAAI87sAAAAAAR53YAAAAAAjzuwAAAAABHndgAAAAACMfa"
                              />
                            </div>
                            <Space direction="vertical">
                              <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={handleLogoUpload}
                                maxCount={1}
                              >
                                <Button icon={<UploadOutlined />}>
                                  {translate(
                                    "tenants.invoiceSettings.fields.uploadLogo",
                                    "Télécharger le logo",
                                  )}
                                </Button>
                              </Upload>
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={handleRemoveLogo}
                              >
                                {translate("buttons.delete", "Supprimer")}
                              </Button>
                            </Space>
                          </div>
                        ) : (
                          <>
                            <div className="w-40 h-40 rounded border border-neutral-600 flex items-center justify-center bg-neutral-800/50">
                              <PictureOutlined className="text-4xl text-neutral-500" />
                            </div>
                            <Upload
                              accept="image/*"
                              showUploadList={false}
                              beforeUpload={handleLogoUpload}
                              maxCount={1}
                            >
                              <Button icon={<UploadOutlined />}>
                                {translate(
                                  "tenants.invoiceSettings.fields.uploadLogo",
                                  "Télécharger le logo",
                                )}
                              </Button>
                            </Upload>
                          </>
                        )}
                      </div>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={
                        <span className="flex items-center gap-1">
                          {translate(
                            "tenants.invoiceSettings.fields.logoUrl",
                            "URL du logo",
                          )}
                          <Tooltip
                            title={translate(
                              "tenants.invoiceSettings.tooltips.logoUrl",
                            )}
                          >
                            <InfoCircleOutlined className="text-neutral-400" />
                          </Tooltip>
                        </span>
                      }
                      name="logoUrl"
                      extra={translate(
                        "invoice-settings.logoUrlHelper",
                        "Alternative à l'upload : entrez l'URL de votre logo hébergé",
                      )}
                    >
                      <Input
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.logoUrl",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider></Divider>

                <Title level={5} className="!my-6">
                  <BgColorsOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.colorPalette",
                    "Palette de couleurs",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.primaryColor",
                        "Couleur principale",
                      )}
                      name="brandColors.primary"
                      getValueFromEvent={(c: { toHexString?: () => string }) =>
                        c?.toHexString?.() || c
                      }
                    >
                      <ColorPicker showText format="hex" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.secondaryColor",
                        "Couleur secondaire",
                      )}
                      name="brandColors.secondary"
                      getValueFromEvent={(c: { toHexString?: () => string }) =>
                        c?.toHexString?.() || c
                      }
                    >
                      <ColorPicker showText format="hex" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.accentColor",
                        "Couleur d'accent",
                      )}
                      name="brandColors.accent"
                      getValueFromEvent={(c: { toHexString?: () => string }) =>
                        c?.toHexString?.() || c
                      }
                    >
                      <ColorPicker showText format="hex" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider></Divider>

                <Title level={5} className="!my-6">
                  <EditOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.customTexts",
                    "Textes personnalisés",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.headerFr",
                        "En-tête (Français)",
                      )}
                      name="headerConfig.fr"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.headerFr",
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.headerEn",
                        "En-tête (Anglais)",
                      )}
                      name="headerConfig.en"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.headerEn",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.footerFr",
                        "Pied de page (Français)",
                      )}
                      name="footerConfig.fr"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.footerFr",
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.footerEn",
                        "Pied de page (Anglais)",
                      )}
                      name="footerConfig.en"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.footerEn",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider></Divider>

                <Title level={5} className="!my-6">
                  <FileTextOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.vatExemptionMention",
                    "Mention d'exonération de TVA",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "invoice-settings.vatMentionLabel",
                        "Mention TVA (Français)",
                      )}
                      name="vatExemptionNotice.fr"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.vatExemptionNoticeFr",
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={translate(
                        "invoice-settings.vatMentionLabelEn",
                        "Mention TVA (Anglais)",
                      )}
                      name="vatExemptionNotice.en"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder={translate(
                          "tenants.invoiceSettings.placeholders.vatExemptionNoticeEn",
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {showIntegrations && activeSegment === "integrations" && (
              <>
                <Title level={5} className="mt-2 !mb-4">
                  <ApiOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "invoice-settings.engines.title",
                    "Moteurs",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={translate(
                        "invoice-settings.invoicingEngine.label",
                        "Moteur de facturation",
                      )}
                      name="invoicingEngine"
                      extra={translate(
                        "invoice-settings.invoicingEngine.helper",
                        "Interne génère les PDF localement. Qonto héberge les factures.",
                      )}
                    >
                      <Select
                        onChange={handleInvoicingEngineChange}
                        options={[
                          {
                            label: translate(
                              "invoice-settings.invoicingEngine.internal",
                              "Interne",
                            ),
                            value: "internal",
                          },
                          {
                            label: translate(
                              "invoice-settings.invoicingEngine.qonto",
                              "Qonto",
                            ),
                            value: "qonto",
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={translate(
                        "invoice-settings.paymentEngine.label",
                        "Moteur de paiement",
                      )}
                      name="paymentEngine"
                      extra={translate(
                        "invoice-settings.paymentEngine.helper",
                        "Stripe pour le paiement par carte. Qonto pour les liens de paiement.",
                      )}
                    >
                      <Select
                        onChange={handlePaymentEngineChange}
                        options={[
                          {
                            label: translate(
                              "invoice-settings.paymentEngine.stripe",
                              "Stripe",
                            ),
                            value: "stripe",
                          },
                          {
                            label: translate(
                              "invoice-settings.paymentEngine.qonto",
                              "Qonto",
                            ),
                            value: "qonto",
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
               
                <Alert
                  className="mb-4"
                  type="info"
                  showIcon
                  message={translate(
                    "invoice-settings.engines.coupledNote",
                    "Le paiement via Qonto impose la facturation Qonto. La facturation interne impose le paiement Stripe.",
                  )}
                />
                {qontoEngineSelected && !qontoConnected && (
                  <Alert
                    className="mb-4"
                    type="warning"
                    showIcon
                    message={translate(
                      "invoice-settings.engines.qontoNotConnected",
                      "Qonto n'est pas encore connecté. Configurez l'intégration ci-dessous.",
                    )}
                  />
                )}
                <Title level={5} className="!mb-4">
                  {translate(
                    "invoice-settings.engines.providersTitle",
                    "Fournisseurs",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} md={12}>
                    <StripeIntegrationCard
                      paymentEngine={selectedPaymentEngine}
                      invoicingEngine={selectedInvoicingEngine}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <QontoIntegrationCard
                      paymentEngine={selectedPaymentEngine}
                      invoicingEngine={selectedInvoicingEngine}
                      onImported={refetch}
                    />
                  </Col>
                </Row>
              </>
            )}
            {showInvoicing && activeSegment === "defaults" && (
              <>
                <Title level={5} className="!my-6">
                  <SettingOutlined
                    className="mr-2"
                    style={{ color: token.colorPrimary }}
                  />
                  {translate(
                    "tenants.invoiceSettings.sections.invoiceDefaults",
                    "Paramètres par défaut",
                  )}
                </Title>
                <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={labelWithTooltip(
                        translate(
                          "tenants.invoiceSettings.fields.defaultVatRate",
                          "Taux de TVA par défaut (%)",
                        ),
                        "tenants.invoiceSettings.tooltips.defaultVatRate",
                      )}
                      name="defaultVatRate"
                      initialValue={20}
                      extra={translate(
                        "tenants.invoiceSettings.tooltips.defaultVatRate",
                        "Standard en France : 20%",
                      )}
                    >
                      <InputNumber min={0} max={100} className="w-full" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={labelWithTooltip(
                        translate(
                          "tenants.invoiceSettings.fields.paymentTermDays",
                          "Délai de paiement (jours)",
                        ),
                        "tenants.invoiceSettings.tooltips.paymentTermDays",
                      )}
                      name="paymentTermDays"
                      initialValue={0}
                      extra={translate(
                        "tenants.invoiceSettings.tooltips.paymentTermDays",
                        "0 = paiement immédiat",
                      )}
                    >
                      <InputNumber min={0} max={365} className="w-full" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate(
                        "tenants.invoiceSettings.fields.defaultLanguage",
                        "Langue par défaut",
                      )}
                      name="defaultLanguage"
                      initialValue="fr"
                    >
                      <Select
                        options={[
                          {
                            label: translate(
                              "invoice-settings.languageFr",
                              "Français",
                            ),
                            value: "fr",
                          },
                          {
                            label: translate(
                              "invoice-settings.languageEn",
                              "Anglais",
                            ),
                            value: "en",
                          },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </GlassCard>
          <Flex
            className="bottom-3 z-30 mt-2 py-3 rounded-xl justify-end"
            gap={12}
          >
            <Button
              type="primary"
              htmlType="submit"
              onClick={handleSave}
              disabled={invoicingLocked}
              className="flex-1 !h-11 text-base font-semibold !rounded-lg"
            >
              {translate("buttons.save", "Enregistrer")}
            </Button>
          </Flex>
        </Spin>
      </Form>
    </div>
  );
};

export default InvoiceSettings;
